import { useEffect, useState } from "react";
import { AlertTriangle, RotateCcw, ScanLine } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const STEPS = ["Uploading drawing", "Scanning every page", "Listing windows & doors", "Pricing from your settings"];
const EST_STEPS = ["Uploading estimate", "Reading every page", "Copying lines & prices", "Checking the subtotal"];

export function ExtractionProgress({ filename, phase, estimate = false }) {
  const closer = phase === "closer_look";
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 0.5), 500);
    return () => clearInterval(t);
  }, []);
  const pct = Math.min(94, Math.round(100 * (1 - Math.exp(-elapsed / 28))));
  const step = elapsed < 2 ? 0 : elapsed < 12 ? 1 : elapsed < 40 ? 2 : 3;
  return (
    <div data-testid="extraction-progress-indicator" className="relative overflow-hidden rounded-2xl border border-slate-800 bg-[#F2F2F7]/80 p-10">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-amber-500/10 to-transparent scan-line" />
      <div className="relative flex flex-col items-center text-center">
        <div className="relative mb-8 grid h-20 w-20 place-items-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-amber-500/15" />
          <span className="absolute inset-2 rounded-full border border-amber-500/40" />
          <ScanLine className="h-8 w-8 text-amber-400" />
        </div>
        <h2 data-testid="extraction-status-text" className="font-display text-2xl font-bold text-slate-50">
          {closer ? "Taking a closer look…" : estimate ? "Copying your estimate…" : "Reading your drawing…"}
        </h2>
        <p data-testid="extraction-status-subtext" className="mt-2 text-sm text-slate-400">
          {filename ? `${filename} · ` : ""}
          {closer ? "re-reading every sheet at high resolution, this can take up to 2 minutes." : "this usually takes 20–60 seconds."}
        </p>
        <Progress value={pct} className="mt-8 h-1.5 w-full max-w-md bg-slate-800 [&>div]:bg-amber-500" />
        <ol className="mt-8 grid w-full max-w-2xl grid-cols-2 gap-3 md:grid-cols-4">
          {(estimate ? EST_STEPS : STEPS).map((s, i) => (
            <li key={s} className={`rounded-lg border px-3 py-2 text-xs transition-colors ${i < step ? "border-emerald-500/30 text-emerald-400" : i === step ? "border-amber-500/40 text-amber-300" : "border-slate-800 text-slate-600"}`}>
              {s}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

const DXF_ERR = "We couldn't read this DXF. Please export it to PDF and try again.";

export function ExtractionError({ message, onRetry, retrying }) {
  const canRetry = message !== DXF_ERR;
  return (
    <div data-testid="extraction-error" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-10 text-center">
      <AlertTriangle className="mx-auto mb-4 h-8 w-8 text-red-400" />
      <h2 className="font-display text-xl font-bold text-slate-50">We couldn't read this drawing</h2>
      <p data-testid="extraction-error-message" className="mx-auto mt-2 max-w-lg text-sm text-slate-400">{message || "The AI reader failed. Please try again."}</p>
      {canRetry && <button data-testid="extraction-retry-button" onClick={onRetry} disabled={retrying}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
        <RotateCcw className="h-4 w-4" /> Try again
      </button>}
    </div>
  );
}
